import classNames from 'classnames'
import _ from 'lodash'
import React from 'react'

import { UI2FieldControl } from 'browser/components/ui2/fields/field-ui/field-control/field-control'
import { TetherTarget } from 'browser/components/atomic-elements/atoms/tether-target'
import { IComponentsContext } from 'browser/contexts/components/components-context'
import { getWebEntityDisplayFromSchema, getWebEntityIconByName } from 'browser/app/models/entity-display-registry'
import { UI2EntitySearchFieldProps } from 'shared-libs/components/ui2/fields/ui2-entity-search-field/ui2-entity-search-field-props'
import { ResolvedSchemaPaths } from 'shared-libs/components/ui2/fields/ui2-entity-search-field/entity-search-utils'

import { EntitySearchSubmitResults } from './entity-search-submit-results'
import { UI2Icon } from 'browser/components/ui2/display/ui2-icon/ui2-icon'
import { UI2IconNames } from 'shared-libs/components/ui2/icons/ui2-icon-names'

import './_entity-search-submit-input.scss'

interface IEntitySearchSubmitInputProps {
  fieldProps: UI2EntitySearchFieldProps
  componentsContext: IComponentsContext
  entitySchema: any
  entityDisplayName: string
  schemaPaths: ResolvedSchemaPaths
  entityDisplayNamePlural: string
  // Submit-mode state from the parent orchestrator
  searchFilter: string
  requestedSearchFilter: string
  isCurrentFilterCommitted: boolean
  isSubmitButtonVisible: boolean
  isSubmitResultsOpen: boolean
  submitWrapperWidth: number
  onSearchFilterChange: (value: string) => void
  onSearchKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
  onSubmitPress: () => void
  onResultsClose: () => void
  onResultsClosed: () => void
  onCommitSelection: (payload: { entity: any; searchFilter: string }) => void
  onCommitRawValue?: (searchFilter: string) => void
  onSubmitSearch?: (hasResults: boolean) => void
  onWrapperRef?: (el: HTMLDivElement | null) => void
}

export class EntitySearchSubmitInput extends React.Component<IEntitySearchSubmitInputProps> {
  private tetherRef: TetherTarget | undefined

  private handleTetherRef = (ref: TetherTarget | null) => {
    this.tetherRef = ref ?? undefined
  }

  public open() {
    this.tetherRef?.open?.()
  }

  public close() {
    this.tetherRef?.close?.()
  }

  public render() {
    const { fieldProps, isCurrentFilterCommitted, searchFilter, entitySchema } = this.props
    const { isDisabled, isLoading, placeholder } = fieldProps
    const {
      isSubmitButtonVisible,
      isSubmitResultsOpen,
      submitWrapperWidth,
      requestedSearchFilter,
    } = this.props

    const canSubmit = this.isSubmitSearchValid(searchFilter) && !isDisabled && !isLoading
    const showPulse =
      isSubmitButtonVisible &&
      canSubmit &&
      !isCurrentFilterCommitted &&
      !isSubmitResultsOpen

    const leadingIconName = getWebEntityIconByName(fieldProps.iconName)
      ?? getWebEntityDisplayFromSchema(entitySchema)?.iconName

    const hideButton =
      isCurrentFilterCommitted && !_.isEmpty(_.trim(searchFilter))

    return (
      <div
        className='ui2-entitySearchSubmitInput'
        ref={this.props.onWrapperRef}
      >
        <UI2FieldControl
          leftContent={
            leadingIconName ? (
              <UI2Icon
                name={leadingIconName}
                size={16}
                className='ui2-entitySearchSubmitInput-leadingIcon'
              />
            ) : undefined
          }
          rightAddon={
            hideButton ? null : (
              <TetherTarget
                ref={this.handleTetherRef}
                closeOnEsc={true}
                closeOnOutsideClick={true}
                closeOnPortalClick={false}
                closeOnTetherTargetClick={false}
                automaticAdjustOffset={true}
                onClose={this.props.onResultsClosed}
                tetherOptions={{
                  attachment: 'top right',
                  targetAttachment: 'bottom right',
                  offset: { top: 8, left: 0 },
                }}
                tethered={
                  <EntitySearchSubmitResults
                    fieldProps={fieldProps}
                    componentsContext={this.props.componentsContext}
                    entitySchema={entitySchema}
                    entityDisplayName={this.props.entityDisplayName}
                    schemaPaths={this.props.schemaPaths}
                    searchFilter={requestedSearchFilter}
                    onCommitSelection={this.props.onCommitSelection}
                    onCommitRawValue={this.props.onCommitRawValue}
                    onSubmitSearch={this.props.onSubmitSearch}
                    onClose={this.props.onResultsClose}
                    width={submitWrapperWidth || undefined}
                  />
                }
              >
                <span className='ui2-entitySearchSubmitInput-buttonWrap'>
                  <button
                    type='button'
                    className={classNames('ui2-entitySearchSubmitInput-button', {
                      'ui2-entitySearchSubmitInput-button--pulse': showPulse,
                      'ui2-entitySearchSubmitInput-button--resultsOpen':
                        isSubmitResultsOpen || !canSubmit,
                    })}
                    onClick={
                      !isSubmitResultsOpen && canSubmit
                        ? () => {
                            this.props.onSubmitPress()
                            this.tetherRef?.open?.()
                          }
                        : undefined
                    }
                    disabled={!canSubmit && !isSubmitResultsOpen}
                    aria-label='Submit search'
                  >
                    <UI2Icon name={UI2IconNames.ARROW_RIGHT} />
                  </button>
                </span>
              </TetherTarget>
            )
          }
        >
          <input
            className={classNames('ui2-entitySearchSubmitInput-input', {
              'ui2-entitySearchSubmitInput-input--pending': !isCurrentFilterCommitted,
            })}
            type='search'
            autoFocus={fieldProps.autoFocus}
            placeholder={placeholder || this.getPlaceholder()}
            value={searchFilter}
            onChange={this.handleChange}
            onKeyDown={this.props.onSearchKeyDown}
            disabled={isDisabled || isLoading}
          />
        </UI2FieldControl>
      </div>
    )
  }

  private handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.props.onSearchFilterChange(event.target.value)
  }

  private isSubmitSearchValid = (searchFilter: string) => {
    const { minQueryLength = 0 } = this.props.fieldProps
    return !_.isEmpty(searchFilter) && searchFilter.length >= minQueryLength
  }

  private getPlaceholder = () => {
    return `Search ${_.lowerCase(this.props.entityDisplayNamePlural)}...`
  }
}
